import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Search, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import BackgroundAnimation from './components/BackgroundAnimation';
import ScamAnalysis from './components/ScamAnalysis';
import './App.css';

// Function to parse n8n workflow response
const parseWorkflowResponse = (result) => {
  console.log('🔧 Parsing workflow response...');
  console.log('📥 Input result:', result);
  
  // Handle different response formats
  let output = '';
  if (Array.isArray(result) && result[0] && result[0].output) {
    output = result[0].output;
  } else if (result && result.output) {
    output = result.output;
  } else if (typeof result === 'string') {
    output = result;
  } else {
    console.log('❌ Invalid result format:', result);
    return {
      scamLikelihoodScore: 5,
      analysisReport: 'Unable to parse analysis results',
      disclaimer: 'Analysis results could not be processed properly.'
    };
  }

  console.log('📄 Raw output text:', output);
  
  // Extract scam likelihood score
  const scoreMatch = output.match(/Risk of website being a scam: (\d+)/);
  const scamLikelihoodScore = scoreMatch ? parseInt(scoreMatch[1]) : 5;
  console.log('🎯 Extracted score:', scamLikelihoodScore, 'from match:', scoreMatch);
  
  // Extract disclaimer
  const disclaimerMatch = output.match(/Disclaimer: ([^\n]+)/);
  const disclaimer = disclaimerMatch ? disclaimerMatch[1] : 'Analysis completed. Please review results carefully.';
  console.log('⚠️ Extracted disclaimer:', disclaimer);
  
  console.log('📋 Using combined analysis report');
  
  const finalResult = {
    scamLikelihoodScore,
    analysisReport: output,
    disclaimer
  };
  
  console.log('✅ Final parsed result:', finalResult);
  return finalResult;
};

// Helper function to extract sections from the output
const extractSection = (text, startMarker, endMarker) => {
  const startIndex = text.indexOf(startMarker);
  if (startIndex === -1) return '';
  
  const endIndex = text.indexOf(endMarker, startIndex);
  const sectionText = endIndex !== -1 
    ? text.substring(startIndex + startMarker.length, endIndex).trim()
    : text.substring(startIndex + startMarker.length).trim();
  
  return sectionText.replace(/^\*\s*/, '').replace(/\n\*\s*/g, '\n• ');
};

function App() {
  const [url, setUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const cheesyMessages = [
    "🔍 Agent 1: Deploying domain detective...",
    "🛡️ Agent 2: Search engine signals specialist activated...",
    "🔎 Agent 3: Pricing pattern analyst on the case...",
    "🤖 Agent 4: Content quality inspector engaged...",
    "⚡ All agents working in perfect harmony...",
    "🎯 Cross-referencing findings across all agents...",
    "🔬 Running advanced correlation algorithms...",
    "🚀 Synthesizing multi-agent intelligence...",
    "💡 The plot thickens as patterns emerge...",
    "🎪 Our digital circus is performing the ultimate analysis...",
    "🌟 Almost there! Finalizing comprehensive report...",
    "🎉 This is interesting... let's inspect this even more!",
    "🔍 Deep dive analysis in progress...",
    "🛡️ Uncovering hidden patterns...",
    "🔎 This website is quite intriguing...",
    "🤖 Our agents are fascinated by these findings...",
    "⚡ This requires extra scrutiny...",
    "🎯 Something interesting caught our attention...",
    "🔬 This is getting more complex than expected...",
    "🚀 Our AI is having an 'aha!' moment..."
  ];

  const startLoadingAnimation = () => {
    let step = 0;
    let startTime = Date.now();
    
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const elapsedSeconds = Math.floor(elapsed / 1000);
      
      // After 30 seconds, switch to "interesting" messages
      if (elapsedSeconds >= 30) {
        const interestingIndex = Math.min(step - 20, cheesyMessages.length - 1);
        setLoadingMessage(cheesyMessages[Math.max(11, interestingIndex)]);
      } else {
        // First 30 seconds: show agent progression (0-20 steps)
        const agentStep = Math.min(step, 20);
        setLoadingMessage(cheesyMessages[agentStep]);
      }
      
      setLoadingStep(step);
      step++;
    }, 1500);

    return interval;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      setError('Please enter a website URL');
      return;
    }

    if (!validateUrl(url)) {
      setError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setError(null);
    setIsAnalyzing(true);
    setAnalysisResult(null);
    
    // Start the cheesy loading animation
    const loadingInterval = startLoadingAnimation();

    try {
      const payload = [{
        "Website URL": url,
        "submittedAt": new Date().toISOString(),
        "formMode": "test"
      }];

      // Direct webhook URL
      const response = await fetch('https://nyrix.app.n8n.cloud/webhook/scamguard-ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Analysis failed. Please try again.');
      }

      const result = await response.json();
      
      // Debug logging
      console.log('🔍 Raw response from n8n webhook:', result);
      console.log('📊 Response type:', typeof result);
      console.log('📋 Response keys:', Object.keys(result));
      
      // Parse the n8n workflow response format
      const parsedResult = parseWorkflowResponse(result);
      console.log('✨ Parsed result:', parsedResult);
      
      setAnalysisResult(parsedResult);
    } catch (err) {
      setError(err.message);
    } finally {
      clearInterval(loadingInterval);
      setIsAnalyzing(false);
      setLoadingMessage('');
      setLoadingStep(0);
    }
  };

  const resetAnalysis = () => {
    setAnalysisResult(null);
    setError(null);
    setUrl('');
  };

  return (
    <div className="app">
      <BackgroundAnimation />
      
      <div className="app-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="header"
        >
          <div className="logo">
            <Shield className="logo-icon" />
            <h1>ScamGuard AI</h1>
          </div>
          <p className="subtitle">
            Advanced multi-agent scam detection system
          </p>
          <p className="powered-by">
            Powered by <strong>Nyrix</strong>
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {!analysisResult ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5 }}
              className="form-container"
            >
              <form onSubmit={handleSubmit} className="analysis-form">
                <div className="input-group">
                  <Search className="input-icon" />
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="Enter website URL to analyze..."
                    className="url-input"
                    disabled={isAnalyzing}
                  />
                </div>
                
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="error-message"
                  >
                    <AlertTriangle className="error-icon" />
                    {error}
                  </motion.div>
                )}

                <motion.button
                  type="submit"
                  className="analyze-button"
                  disabled={isAnalyzing || !url.trim()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="button-icon spinning" />
                      {loadingMessage || "Analyzing..."}
                    </>
                  ) : (
                    <>
                      <Shield className="button-icon" />
                      Analyze Website
                    </>
                  )}
                </motion.button>
              </form>

              {isAnalyzing && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="loading-section"
                >
                  <div className="loading-progress">
                    <div className="progress-bar">
                      <motion.div 
                        className="progress-fill"
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 35, ease: "easeInOut" }}
                      />
                    </div>
                    <div className="loading-stats">
                      <div className="stat-item">
                        <span className="stat-number">{Math.min(Math.floor((loadingStep * 1.5) / 6.67) + 1, 4)}</span>
                        <span className="stat-label">Agents Deployed</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{Math.min(Math.floor((loadingStep * 1.5) * 3.33), 100)}%</span>
                        <span className="stat-label">Analysis Complete</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              <div className="how-it-works">
                <h3>How Our Multi-Agent System Works</h3>
                <p className="system-description">
                  Our AI-powered system uses four specialized agents that work simultaneously to analyze websites from different angles:
                </p>
                <div className="agents-grid">
                  <div className="agent-card">
                    <div className="agent-header">
                      <Shield className="agent-icon" />
                      <h4>Domain & Technical Details</h4>
                    </div>
                    <p>Analyzes domain age, SSL certificates, and suspicious TLDs (.xyz, .top, .click) to identify technical red flags.</p>
                  </div>
                  
                  <div className="agent-card">
                    <div className="agent-header">
                      <Search className="agent-icon" />
                      <h4>Search Engine Signals</h4>
                    </div>
                    <p>Scans Reddit, Trustpilot, ScamAdviser, and SiteJabber for scam reports and user complaints.</p>
                  </div>
                  
                  <div className="agent-card">
                    <div className="agent-header">
                      <AlertTriangle className="agent-icon" />
                      <h4>Product & Pricing Patterns</h4>
                    </div>
                    <p>Detects exaggerated discounts, fake endorsements, and suspiciously low prices for high-end brands.</p>
                  </div>
                  
                  <div className="agent-card">
                    <div className="agent-header">
                      <CheckCircle className="agent-icon" />
                      <h4>Content Analysis</h4>
                    </div>
                    <p>Evaluates reviews for duplicates, checks grammar quality, and verifies company information authenticity.</p>
                  </div>
                </div>
                
                <div className="evaluation-process">
                  <p className="process-description">
                    All findings are then merged and evaluated by our central AI evaluator, which assigns a comprehensive scam-likelihood score (1-10) along with a detailed analysis report.
                  </p>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5 }}
            >
              <ScamAnalysis 
                result={analysisResult} 
                onReset={resetAnalysis}
                originalUrl={url}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default App;
